/**
 * SiteMap — a project's real site map: the Mapbox static map the mobile app's
 * project cards draw (`backend/src/frontend/lib/jobSiteMap.ts`, same token,
 * style and zoom), centred on the project.
 *
 * The centre is the project's own coordinates; a project with only an address
 * is geocoded once through Mapbox (US-processed, the same vendor rule the
 * mobile card follows — never Nominatim), cached per address for the page's
 * life. No device location is read. A project with neither, or a map that
 * fails to load, falls back to the drawn street grid (`MapThumb`), so a tile
 * is never blank.
 */

import { useEffect, useState } from "react";
import {
  getJobSiteMapUrl,
  getMapboxToken,
  normalizeJobSiteLocation,
} from "../../../../backend/src/frontend/lib/jobSiteMap";
import { MapThumb, mapSeed } from "./MapThumb";

const TOKEN = getMapboxToken().trim();
const geocoded = new Map<string, Promise<{ lat: number; lng: number } | null>>();

function geocode(address: string): Promise<{ lat: number; lng: number } | null> {
  const key = address.trim().toLowerCase();
  const known = geocoded.get(key);
  if (known) return known;
  const params = new URLSearchParams({ access_token: TOKEN, limit: "1" });
  const request = fetch(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?${params.toString()}`,
    { headers: { Accept: "application/json" } },
  )
    .then((response) => (response.ok ? response.json() : null))
    .then((data) => {
      const center = data?.features?.[0]?.center;
      return Array.isArray(center) && center.length === 2 ? { lat: Number(center[1]), lng: Number(center[0]) } : null;
    })
    .catch(() => null);
  geocoded.set(key, request);
  return request;
}

export function SiteMap({
  id,
  location,
  address,
  size = 260,
}: {
  /** Seeds the drawn fallback, so a project always draws the same streets. */
  id: string;
  location?: { lat: number; lng: number } | null;
  address?: string | null;
  size?: number;
}) {
  const own = normalizeJobSiteLocation(location ?? null);
  const [centre, setCentre] = useState<{ lat: number; lng: number } | null>(own);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
    if (own) {
      setCentre(own);
      return;
    }
    setCentre(null);
    if (!address?.trim() || !TOKEN) return;
    let live = true;
    void geocode(address).then((coords) => {
      if (live) setCentre(normalizeJobSiteLocation(coords));
    });
    return () => {
      live = false;
    };
    // `own` is derived from `location`; key on the values, not the object.
  }, [location?.lat, location?.lng, address]);

  const url = centre && !failed ? getJobSiteMapUrl(centre, TOKEN) : null;
  // Fills its (position: relative) parent; the map's centre stays the strip's
  // centre, which is where the caller's pin sits.
  if (!url) {
    return (
      <span style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)", display: "block" }}>
        <MapThumb seed={mapSeed(id)} size={size} />
      </span>
    );
  }
  return (
    <img
      src={url}
      alt=""
      aria-hidden
      data-testid="site-map"
      loading="lazy"
      onError={() => setFailed(true)}
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", display: "block" }}
    />
  );
}
